import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    CircularProgress,
    Button,
    InputBase
} from '@mui/material';
import { MoreVertIcon, EditOutlinedIcon, DeleteIcon, PersonAddIcon, SearchOutlinedIcon, AddIcon } from 'icons/materialIcons';
import PropTypes from 'prop-types';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';

const TeamTemplateList = ({
    templates = [],
    isLoading = false,
    onEdit,
    onDelete,
    onAssignMembers,
    searchQuery = '',
    onSearchChange
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Filter templates based on search query
    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) {
            return templates;
        }
        const query = searchQuery.toLowerCase();
        return templates.filter(template => {
            const templateName = (template.templateName || '').toLowerCase();
            return templateName.includes(query);
        });
    }, [templates, searchQuery]);

    const handleMenuOpen = (event, template) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedTemplate(template);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTemplate(null);
    };

    const handleEdit = () => {
        if (selectedTemplate) {
            onEdit(selectedTemplate);
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (selectedTemplate) {
            onDelete(selectedTemplate._id);
        }
        handleMenuClose();
    };

    const handleAssignMembers = () => {
        if (selectedTemplate) {
            onAssignMembers(selectedTemplate);
        }
        handleMenuClose();
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2, color: '#666' }}>Loading templates...</Typography>
            </Box>
        );
    }

    if (templates.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                border: '0.0625rem dashed #e0e0e0',
                borderRadius: '0.75rem',
                backgroundColor: '#fafafa'
            }}>
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    No templates found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Create your first template by clicking the 'Add Template' button above
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Search Bar */}
            {onSearchChange && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        border: '1px solid #e3e3e3',
                        padding: '8px 16px',
                        marginBottom: '12px',
                        '&:hover': {
                            boxShadow: 2,
                        },
                    }}
                >
                    <SearchOutlinedIcon
                        sx={{
                            color: '#828282',
                            fontSize: '18px',
                            marginRight: '8px'
                        }}
                    />
                    <InputBase
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search templates..."
                        sx={{
                            fontSize: '0.7rem',
                            color: '#4f4f4f',
                            '& .MuiInputBase-input::placeholder': {
                                color: '#828282',
                                opacity: 1,
                            },
                        }}
                    />
                </Box>
            )}

            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <Table aria-label="templates table">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666', width: '60%' }}>Template Info</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666', width: '20%', textAlign: 'center', padding: '12px 0px 12px 16px' }}>Template</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#666', width: '20%', textAlign: 'center', padding: '12px 16px 12px 0px' }}>Members</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTemplates.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ padding: 4 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {searchQuery ? 'No templates found matching your search' : 'No templates found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredTemplates.map((template) => (
                                <TableRow
                                    key={template._id}
                                    hover
                                    onClick={() => onEdit(template)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa'
                                        },
                                        '&:last-child td, &:last-child th': {
                                            border: 0
                                        }
                                    }}
                                >
                                    <TableCell component="th" scope="row" sx={{ width: '60%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                                {template.templateName || 'Unnamed Template'}
                                            </Typography>
                                            {template.isDefault && (
                                                <Chip
                                                    label="Default"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        fontSize: '0.7rem',
                                                        height: '20px',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: '20%', textAlign: 'center', padding: '12px 0px 12px 16px' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <TeamWhiteButtonWithIcon
                                            text="Edit Template"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit(template);
                                            }}
                                            padding="8px 16px"
                                        />
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: '20%', textAlign: 'center', padding: '12px 16px 12px 0px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                            {template.assignedMembers && template.assignedMembers.length > 0 ? (
                                            <Button
                                                variant="contained"
                                                    endIcon={<AddIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onAssignMembers(template);
                                                }}
                                                sx={{
                                                    backgroundColor: 'white',
                                                    color: '#4f4f4f',
                                                    borderRadius: '20px',
                                                    border: '1px solid #e3e3e3',
                                                    boxShadow: 'none',
                                                    textTransform: 'none',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '600',
                                                    padding: '8px 16px',
                                                    '&:hover': {
                                                        backgroundColor: 'white',
                                                        boxShadow: 2,
                                                    },
                                                        '& .MuiButton-endIcon': {
                                                        '& > *:first-of-type': {
                                                            fontSize: '16px',
                                                            color: '#828282'
                                                        }
                                                    }
                                                }}
                                                >
                                                    {`${template.assignedMembers.length} member${template.assignedMembers.length === 1 ? '' : 's'}`}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onAssignMembers(template);
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                        borderRadius: '20px',
                                                        border: 'none',
                                                        boxShadow: 'none',
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '600',
                                                        padding: '8px 16px',
                                                        '&:hover': {
                                                            backgroundColor: '#333',
                                                            boxShadow: 2,
                                                    }
                                                }}
                                            >
                                                Assign Members
                                            </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        minWidth: '180px',
                    }
                }}
            >
                <MenuItem onClick={handleEdit}>
                    <EditOutlinedIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Edit Template
                </MenuItem>
                <MenuItem onClick={handleAssignMembers}>
                    <PersonAddIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Assign Members
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <DeleteIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Delete
                </MenuItem>
            </Menu>
        </Box>
    );
};

TeamTemplateList.propTypes = {
    templates: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAssignMembers: PropTypes.func.isRequired,
    searchQuery: PropTypes.string,
    onSearchChange: PropTypes.func
};

export default TeamTemplateList;

